import { InjectionToken } from '../../injection';

/**
 * Storage service that acts as a proxy over localStorage and sessionStorage in order to persist data in the browser.
 * This class needs to be used all the time if we need to persist data, and never use localStorage or sessionStorage directly.
 */
export interface IMobizStorageService {
  /**
   * A property to control if sessionStorage should be used instead of localStorage.
   */
  useSessionStorage: boolean;

  /**
   * @param key The key of the object that will be saved
   * @param object The object that will be saved within the storage
   * @param persistOnClear If set to true, the value for the key will be persisted when we clear the storage.
   */
  set<T>(key: string, object: T, persistOnClear?: boolean): void;

  /**
   * @param key The key of the object that will be retrieved
   */
  get<T>(key: string): T | null;

  /**
   * @param key The key of the object that will be removed
   */
  remove(key: string): void;

  /**
   * Register key to be removed when storage is cleared.
   * @param key The key that will be removed.
   */
  registerKeyForClear(key: string): void;

  /**
   * Unregister key from the list of keys that needs to be removed on storage clear.
   * @param key The key that will not be removed.
   */
  unregisterKeyForClear(key: string): void;

  /**
   * Get registered keys that will be removed on clear storage.
   */
  getKeysToClear(): Set<string>;

  /**
   * A method to clear a local storage entry.
   * @param key The key that will be removed from storage.
   */
  clearKey(key: string): void;

  /**
   * A method to clear all local storage and session storage data.
   */
  clear(): void;
}

export const IMobizStorageService = new InjectionToken('IMobizStorageService');
